import {
  List,
  TextField,
  EditButton,
  SimpleList,
  Datagrid,
  ReferenceField,
  FunctionField,
  TextInput,
  BooleanInput,
  ReferenceArrayInput,
  SelectArrayInput,
  TopToolbar,
} from "react-admin";
import { EmptyList } from "server/components";
import { Box, useMediaQuery } from "@mui/material";
import { MobileListRow } from "features/MobileListRow";
import { AdministrationServicePath, SELECT_CITIZENSHIP, MOBILE_BREAKPOINT, CANDIDATE_USERS } from "utils/constants";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import permissions from "app/permissions";
import EditEmailDialog from "../components/EditModals/EditEmailDialog";
import { settings } from "app";
import { accountRequests } from "api/accountApi";
import PasswordResetAction from "../components/PasswordResetAction/PasswordResetAction";
import SecuredComponent from "server/security/SecuredComponent";
import { determineGender, getDisplayName } from "utils/helpers";
import { UsersExporter } from "../XlsxExporter";
import { ExportXlsButton } from "features/ExportXlsxButton";
import CandidaciesDialog from "../components/CandidaciesDialog/CandidaciesDialog";
import { administrationRequests } from "api/administrationApi";

const userFilters = [
  <TextInput
    label="ЕГН"
    source="personId"
    style={{ width: 200 }}
    alwaysOn
  />,
  <TextInput
    label="Име"
    source="firstName"
    style={{ width: 250 }}
    alwaysOn
  />,
  <TextInput
    label="Фамилия"
    source="lastName"
    style={{ width: 250 }}
    alwaysOn
  />,
  <TextInput
    label="Имейл"
    source="email"
    style={{ width: 350 }}
    alwaysOn
  />,
  <ReferenceArrayInput
    label="Гражданство"
    source="citizenshipIds"
    reference={administrationRequests.getCitizenshipSelect()}
    alwaysOn
  >
    <SelectArrayInput
      label="Гражданство"
      optionText="name"
      style={{ width: 250 }}
    />
  </ReferenceArrayInput>,
  <BooleanInput
    source="onlyNoLoggedIn"
    label="Покажи само потребители без вход"
    alwaysOn
  />,
];

const ListUsers = ({ userRole }) => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const userPermissions = getPermissions();
  const hasEditUsersPermission = checkPermission(userPermissions, permissions.canUpdateUsers);

  const ListActions = () => {
    return (
      <TopToolbar>
        <ExportXlsButton exporter={UsersExporter(CANDIDATE_USERS)} />
      </TopToolbar>
    );
  };

  return (
    <List
      actions={<ListActions />}
      title="Кандидат-студенти"
      sx={settings.style.expandStyle}
      filters={userFilters}
      filter={{ Role: userRole }}
    >
      {isMobile ? (
        <SimpleList
          empty={<EmptyList />}
          linkType={false}
          sx={settings.style.mobileList}
          primaryText={record => (
            <MobileListRow
              fields={[
                { label: "ЕГН", value: record.personId },
                { label: "Имейл", value: record.email },
              ]}
            />
          )}
          secondaryText={record => (
            <>
              <div>
                <MobileListRow
                  fields={[
                    { label: "Име", value: record.firstName },
                    { label: "Презиме", value: record.secondName },
                    { label: "Фамилия", value: record.lastName },
                  ]}
                />
                <MobileListRow fields={[{ label: "Пол", value: determineGender(record.gender) }]} />
                <MobileListRow fields={[{ label: "Google имейл", value: record.externalEmail }]} />
                <MobileListRow fields={[{ label: "Тел. номер", value: record.contactPhone }]} />
                <MobileListRow
                  fields={[
                    {
                      label: "Гражданство",
                      value: (
                        <ReferenceField
                          source="citizenshipId"
                          resource={AdministrationServicePath + SELECT_CITIZENSHIP}
                          reference={AdministrationServicePath + SELECT_CITIZENSHIP}
                          label="Гражданство"
                        />
                      ),
                    },
                  ]}
                />
              </div>

              {hasEditUsersPermission && (
                <FunctionField
                  label="Действия"
                  render={record => (
                    <>
                      <SecuredComponent permission={permissions.canResetUsersPasswords}>
                        <PasswordResetAction userId={record.userId} />
                      </SecuredComponent>
                      <EditEmailDialog
                        request={accountRequests.changeUserEmail(record?.userId)}
                        hasEditUsersPermission={hasEditUsersPermission}
                        userRecord={record}
                      />

                      <EditButton
                        label=""
                        record={{ id: record.userId }}
                      />
                    </>
                  )}
                />
              )}
              <SecuredComponent permission={permissions.canPreviewCandidacies}>
                <CandidaciesDialog userId={record?.userId} />
              </SecuredComponent>
            </>
          )}
        />
      ) : (
        <Datagrid
          bulkActionButtons={false}
          rowClick={false}
          sx={settings.style.compactDatagridCells}
        >
          <TextField
            source="personId"
            label="ЕГН"
            sortable={false}
          />
          {/* One "Имена" column instead of three - saves width so the table fits a 14" screen. */}
          <FunctionField
            label="Имена"
            render={getDisplayName}
          />
          <FunctionField
            label="Пол"
            render={record => determineGender(record.gender)}
          />
          <TextField
            source="email"
            label="Имейл"
            sortable={false}
          />
          <TextField
            source="externalEmail"
            label="Google имейл"
            sortable={false}
          />
          <TextField
            source="contactPhone"
            label="Тел. номер"
            sortable={false}
          />
          <ReferenceField
            source="citizenshipId"
            resource={AdministrationServicePath + SELECT_CITIZENSHIP}
            reference={AdministrationServicePath + SELECT_CITIZENSHIP}
            label="Гражданство"
          />

          {/* All row actions share one column (incl. "Виж кандидатури"); below xl they stack vertically. */}
          <FunctionField
            label="Действия"
            render={record => (
              <Box sx={settings.style.stackingActionButtons}>
                {hasEditUsersPermission && (
                  <>
                    <SecuredComponent permission={permissions.canResetUsersPasswords}>
                      <PasswordResetAction userId={record.userId} />
                    </SecuredComponent>
                    <EditEmailDialog
                      request={accountRequests.changeUserEmail(record?.userId)}
                      hasEditUsersPermission={hasEditUsersPermission}
                      userRecord={record}
                    />
                    <EditButton record={{ id: record.userId }} />
                  </>
                )}
                <SecuredComponent permission={permissions.canPreviewCandidacies}>
                  <CandidaciesDialog userId={record?.userId} />
                </SecuredComponent>
              </Box>
            )}
          />
        </Datagrid>
      )}
    </List>
  );
};
export default ListUsers;
